"use client";

import * as React from "react";

import {
  amountMaxFor,
  suggestedMonthlyCap,
  suggestedMonthlyRevenue,
  todayIso,
} from "@/lib/pricing-calculator/defaults";
import type { RepaymentStructure } from "@/lib/pricing-calculator/model";
import type { CalculatorState } from "@/lib/pricing-calculator/url-state";
import {
  isCalculatorParam,
  searchParamsFromState,
  stateFromSearchParams,
  structureFromSearchParams,
} from "@/lib/pricing-calculator/url-state";

const URL_SYNC_DELAY_MS = 300;

function applyPatch(
  previous: CalculatorState,
  patch: Partial<CalculatorState>
): CalculatorState {
  const next: CalculatorState = { ...previous, ...patch };

  // Each currency has its own ceiling; moving to one with a lower ceiling
  // brings the amount down to it rather than leaving it out of range.
  next.amount = Math.min(next.amount, amountMaxFor(next.currency));

  if ("monthlyCap" in patch || "capEnabled" in patch) {
    next.capTouched = true;
  }
  if ("monthlyRevenue" in patch) {
    next.revenueTouched = true;
  }

  const amountChanged =
    next.amount !== previous.amount || next.feePercent !== previous.feePercent;

  // Until the visitor sets a cap themselves, keep suggesting one that scales
  // with the total so the comparison stays meaningful as the amount changes.
  if (!next.capTouched && amountChanged) {
    next.monthlyCap = suggestedMonthlyCap(next.amount, next.feePercent);
  }

  // Likewise keep the revenue prefill plausible for the amount: a 1m advance
  // against 50k monthly revenue would forecast years of repayment.
  if (!next.revenueTouched && next.amount !== previous.amount) {
    next.monthlyRevenue = suggestedMonthlyRevenue(next.amount);
  }

  return next;
}

/**
 * Calculator state, hydrated from the query string after mount so the page
 * itself stays static. `state` is `null` during server render and the first
 * client paint; consumers show a neutral placeholder until it resolves.
 */
export function useCalculatorState(
  defaultCurrency: string,
  defaultStructure?: RepaymentStructure
) {
  const [state, setState] = React.useState<CalculatorState | null>(null);
  const todayRef = React.useRef<string>("");

  React.useEffect(() => {
    const today = todayIso();
    todayRef.current = today;
    const params = new URLSearchParams(window.location.search);
    const initial = stateFromSearchParams(params, today, defaultCurrency);
    // A shared link's valid structure wins; otherwise use the host page's preset.
    if (defaultStructure && !structureFromSearchParams(params)) {
      initial.structure = defaultStructure;
    }
    setState(initial);
  }, [defaultCurrency, defaultStructure]);

  React.useEffect(() => {
    if (!state) return;
    const timer = window.setTimeout(() => {
      // Keep anything the calculator doesn't own (UTMs, click ids) so
      // attribution survives to the signup and contact forms.
      const params = new URLSearchParams();
      for (const [key, value] of new URLSearchParams(window.location.search)) {
        if (!isCalculatorParam(key)) params.append(key, value);
      }
      for (const [key, value] of searchParamsFromState(
        state,
        todayRef.current,
        defaultCurrency
      )) {
        params.set(key, value);
      }
      const query = params.toString();
      const url = `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`;
      if (
        url !==
        `${window.location.pathname}${window.location.search}${window.location.hash}`
      ) {
        window.history.replaceState(window.history.state, "", url);
      }
    }, URL_SYNC_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [state, defaultCurrency]);

  const update = React.useCallback((patch: Partial<CalculatorState>) => {
    setState((previous) => (previous ? applyPatch(previous, patch) : previous));
  }, []);

  return { state, update };
}
